import React, { useEffect, useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemText, ListItemIcon } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { Language } from '@/Language';
import LocalStorageService from '@/services/LocalStorageService';

type LangCode = 'fr' | 'en' | 'zh' | 'ar';

const LANG_OPTIONS: Array<{ code: LangCode; label: string }> = [
  { code: Language.FR as LangCode, label: 'Français' },
  { code: Language.EN as LangCode, label: 'English' },
  { code: Language.ZH as LangCode, label: '中文' },
  { code: Language.AR as LangCode, label: 'العربية' },
];

interface LanguageSwitcherProps {
  /** Couleur de l'icône (utile sur fonds sombres). Par défaut: hérite. */
  color?: string;
  size?: 'small' | 'medium' | 'large';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ color, size = 'medium' }) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const service = useMemo(() => LocalStorageService.getService(), []);

  // Initialisation : si l'utilisateur n'a pas de pref persistée, on s'aligne sur
  // la langue du navigateur (limité aux 4 langues supportées). Sinon on applique
  // la pref persistée.
  useEffect(() => {
    const persisted = service.getLanguage() as LangCode | null;
    if (persisted) {
      if (persisted !== i18n.language) i18n.changeLanguage(persisted);
      return;
    }
    const browser = (window.navigator.language || '').toLowerCase();
    const detected: LangCode =
      browser.startsWith('en') ? 'en'
      : browser.startsWith('zh') ? 'zh'
      : browser.startsWith('ar') ? 'ar'
      : 'fr';
    if (detected !== i18n.language) i18n.changeLanguage(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code: LangCode) => {
    i18n.changeLanguage(code);
    service.setLanguage(code);
    handleClose();
  };

  const current = (i18n.language || 'fr').slice(0, 2) as LangCode;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size={size}
        aria-label="Change language"
        sx={{ color: color ?? 'inherit' }}
      >
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LANG_OPTIONS.map(({ code, label }) => (
          <MenuItem
            key={code}
            selected={current === code}
            onClick={() => handleSelect(code)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {current === code ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
